import { CompareScoreDistance } from "../utils/CompareScoreDistance";

export class TreeNode {
  constructor() {
    this.fObj = null;
    this.fScore = 0;
    this.fChildren = [];
    this.fParent = null;
    this.fBreak = false;
    this.fNumLevels = 1;
    this.fFamilySize = 1;
  }

  InitNode(obj, parentNode, recursive) {
    this.fObj = obj;
    this.fParent = parentNode;
    this.fBreak = false;

    var childList = obj.GetChildList(true);
    var numChildren = childList.length;
    if (recursive) {
      for (var i = 0; i < numChildren; i++) {
        var child = childList[i];
        var childNode = new TreeNode();
        childNode.InitNode(child, this, recursive);
        this.AddChild(childNode);
      }
    }

    // Compute scores incrementally from already-initialized children — O(numChildren) not O(subtree)
    var familySize = 1;
    var maxChildLevels = 0;
    for (var i = 0; i < this.fChildren.length; i++) {
      familySize += this.fChildren[i].fFamilySize;
      if (this.fChildren[i].fNumLevels > maxChildLevels) {
        maxChildLevels = this.fChildren[i].fNumLevels;
      }
    }
    this.fFamilySize = familySize;
    this.fNumLevels = this.fChildren.length > 0 ? 1 + maxChildLevels : 1;

    this.InitializeScore();
  }

  // Compute fScore from already-set fFamilySize and fNumLevels — O(1)
  InitializeScore() {
    var scoreFactor = 1.0;
    var numChildren = this.NumChildren();
    if (numChildren > 10) scoreFactor = 0.7 * scoreFactor;
    if (numChildren > 10 && this.fNumLevels > 3) scoreFactor *= 0.4 * this.fNumLevels;
    if (this.fNumLevels > 4) scoreFactor *= 2;
    this.fScore = this.fFamilySize * scoreFactor;
  }

  /**
   * @return {number}
   */
  NumChildren() {
    return this.fChildren.length;
  }

  /*
        This is where scoring can be manipulated to influence page selection

     */
  CalculateScore() {
    this.fFamilySize = this.CalculateFamilySize();
    this.fNumLevels = this.CalculateNumLevels();
    this.InitializeScore();
    return this.fScore;
  }

  /**
   * @return {number}
   */
  CalculateNumLevels() {
    // Iterative post-order traversal to avoid stack overflow on deep trees
    var stack = [{ node: this, childIdx: 0, childMax: 0 }];

    while (stack.length > 0) {
      var frame = stack[stack.length - 1];
      var node = frame.node;
      var numChildren = node.NumChildren();

      // Find next non-break child to process
      var pushed = false;
      while (frame.childIdx < numChildren) {
        var child = node.fChildren[frame.childIdx];
        frame.childIdx++;
        if (!child.fBreak) {
          stack.push({ node: child, childIdx: 0, childMax: 0 });
          pushed = true;
          break;
        } else {
          frame.childMax = Math.max(frame.childMax, 1);
        }
      }

      if (!pushed) {
        // All children processed
        stack.pop();
        var result = 1 + frame.childMax;
        if (stack.length > 0) {
          stack[stack.length - 1].childMax = Math.max(stack[stack.length - 1].childMax, result);
        } else {
          return result;
        }
      }
    }

    return 1;
  }

  /**
   * @return {number}
   */
  CalculateFamilySize() {
    // Iterative post-order traversal to avoid stack overflow on deep trees
    var stack = [{ node: this, childIdx: 0, size: 1 }];

    while (stack.length > 0) {
      var frame = stack[stack.length - 1];
      var node = frame.node;
      var numChildren = node.NumChildren();

      // Find next child to process
      var pushed = false;
      while (frame.childIdx < numChildren) {
        var child = node.fChildren[frame.childIdx];
        frame.childIdx++;
        if (!child.fBreak) {
          stack.push({ node: child, childIdx: 0, size: 1 });
          pushed = true;
          break;
        } else {
          frame.size += 1;
        }
      }

      if (!pushed) {
        // All children processed
        stack.pop();
        var result = frame.size;
        if (stack.length > 0) {
          stack[stack.length - 1].size += result;
        } else {
          return result;
        }
      }
    }

    return 1;
  }

  AddChild(childNode) {
    childNode.fParent = this;
    this.fChildren.push(childNode);
    this.fScore += childNode.fScore;
  }

  Clone(recursive) {
    if (typeof recursive === "undefined") {
      recursive = true;
    }

    // Shallow clone (non-recursive)
    if (!recursive || this.fBreak) {
      var c = new TreeNode();
      c.fObj = this.fObj;
      c.fParent = this.fParent;
      c.fScore = this.fScore;
      c.fBreak = this.fBreak;
      c.fNumLevels = this.fNumLevels;
      c.fFamilySize = this.fFamilySize;
      return c;
    }

    // Iterative deep clone using explicit stack to avoid stack overflow
    var rootClone = new TreeNode();
    rootClone.fObj = this.fObj;
    rootClone.fParent = this.fParent;
    rootClone.fScore = this.fScore;
    rootClone.fBreak = this.fBreak;
    rootClone.fNumLevels = this.fNumLevels;
    rootClone.fFamilySize = this.fFamilySize;

    var stack = [{ original: this, clone: rootClone }];

    while (stack.length > 0) {
      var frame = stack.pop();
      var orig = frame.original;
      var cloneNode = frame.clone;

      if (orig.fBreak) continue;

      for (var i = 0; i < orig.fChildren.length; i++) {
        var child = orig.fChildren[i];
        var childClone = new TreeNode();
        childClone.fObj = child.fObj;
        childClone.fParent = cloneNode;
        childClone.fScore = child.fScore;
        childClone.fBreak = child.fBreak;
        childClone.fNumLevels = child.fNumLevels;
        childClone.fFamilySize = child.fFamilySize;
        cloneNode.fChildren.push(childClone);

        if (!child.fBreak && child.fChildren.length > 0) {
          stack.push({ original: child, clone: childClone });
        }
      }
    }

    return rootClone;
  }

  // Breaks away node from parents.
  // create a new node at the break point and updates parents accordingly.
  BreakAway(node) {
    var repNode = node.Clone(false);
    repNode.fBreak = true;

    var parentNode = repNode.fParent;
    for (var i = 0; i < parentNode.NumChildren(); i++) {
      var child = parentNode.fChildren[i];
      if (child.fObj === node.fObj) {
        parentNode.fChildren.splice(i, 1, repNode);
        break;
      }
    }

    repNode.fFamilySize = 1;
    repNode.fNumLevels = 1;
    repNode.fScore = 1.0;

    var familySizeDelta = node.fFamilySize - 1;
    while (parentNode !== null) {
      parentNode.fFamilySize -= familySizeDelta;
      var maxChildLevels = 0;
      for (var i = 0; i < parentNode.fChildren.length; i++) {
        var c = parentNode.fChildren[i];
        var cLevels = c.fBreak ? 1 : c.fNumLevels;
        if (cLevels > maxChildLevels) maxChildLevels = cLevels;
      }
      parentNode.fNumLevels = parentNode.fChildren.length > 0 ? 1 + maxChildLevels : 1;
      parentNode.InitializeScore();
      parentNode = parentNode.fParent;
    }

    this.InitializeScore();
  }

  Partition(maxScore, node1, node2) {}

  // Returns a sorted array of all nodes and their children which have number of children greater than minChild.
  // The list is sorted with score with highest scores first
  GetSortedNodes(minChild) {
    var nodeArray = [];
    this.AddToArray(nodeArray, minChild);
    nodeArray.sort(CompareScoreDistance);
    return nodeArray;
  }

  // returns a list of candidate nodes where their scores are either equal or less than scoreThreshold
  // It returns however many nodes with scores equal to scoreThreshold but if after having all these nodes
  // counted, the number of nodes are still less than minNumbNodes, it adds nodes with scores less than scoreThreshold
  GetCandidateNodes(scoreThreshold, minNumNodes) {
    var candidateNodes = [];

    var nodeArray = this.GetSortedNodes(2);

    if (nodeArray.length === 0) {
      nodeArray = this.GetSortedNodes(1);
    }

    if (nodeArray.length > 0) {
      candidateNodes.push(nodeArray[0]);
    }

    return candidateNodes;
  }

  // Adds this node, and those of non-breaking children, to the nodeArray if number of children of each node
  // is at least minChild nodes
  AddToArray(nodeArray, minChild) {
    // Iterative traversal to avoid stack overflow on deep trees
    var stack = [this];

    while (stack.length > 0) {
      var node = stack.pop();
      var numChildren = node.NumChildren();
      if (numChildren >= minChild) {
        nodeArray.push(node);
      }

      if (!node.fBreak) {
        // Push children in reverse order so leftmost is processed first
        for (var i = numChildren - 1; i >= 0; i--) {
          stack.push(node.fChildren[i]);
        }
      }
    }
  }
}
